import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    ExerciceDto,
    ExerciceInfos,
    ExerciceModelDto,
    MutationGetListeExerciceBaseModelArgs,
    MutationGetListeExerciceInfosArgs,
    MutationGetListExerciceGeneriqueArgs,
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import {
    ExerciceStateResetAction,
    GetExercicesGeneriqueListAction,
    GetListeExerciceBaseModelAction,
    GetListeExerciceInfosAction
} from 'src/app/_shared/stores/exercices/exercices.action';
import orderBy from 'lodash-es/orderBy';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { Injectable } from '@angular/core';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { KalDate } from '@kalidea/kaligraphi';
import { getKalDateFromObject, isBetween } from 'src/app/utils/date.utils';

export class ExerciceStateModel {

    loading = false;

    exercices: ExerciceDto[] = [];

    exercicesInfos: ExerciceInfos[] = [];

    exercicesBaseModel: ExerciceModelDto[] = [];

}

function getExerciseFromSelectedDate(exercisesList: ExerciceInfos[], date: KalDate): ExerciceInfos | null {
    return exercisesList?.find(e => {
        return isBetween(date, getKalDateFromObject(e, 'DateDebutExercice'), getKalDateFromObject(e, 'DateFinExercice'));
    }) || null;
}

@Injectable()
@State<ExerciceStateModel>({
    name: 'exerciceState',
    defaults: new ExerciceStateModel()
})
export class ExerciceState {

    constructor(private graphqlService: GraphqlService) {
    }

    // <editor-fold defaultstate="collapsed" desc="Selectors">

    @Selector()
    public static isLoading(state: ExerciceStateModel): boolean {
        return state.loading;
    }

    @Selector()
    public static exercices(state: ExerciceStateModel): ExerciceDto[] {
        return state.exercices;
    }

    @Selector()
    public static exercicesInfos(state: ExerciceStateModel): ExerciceInfos[] {
        return state.exercicesInfos;
    }

    @Selector()
    public static currentExercice(state: ExerciceStateModel): ExerciceInfos {
        const now = new KalDate();

        return state.exercicesInfos.find(e => {
            const startDate: KalDate = getKalDateFromObject(e, 'DateDebutExercice');
            const endDate: KalDate = getKalDateFromObject(e, 'DateFinExercice');

            return now.isBetween(startDate, endDate);
        });
    }

    @Selector()
    public static exercicesBaseModel(state: ExerciceStateModel): ExerciceModelDto[] {
        return state.exercicesBaseModel;
    }

    @Selector()
    public static getExerciseFromDate(date: KalDate): (state) => ExerciceInfos | null {
        return ({exerciceState}) => {
            return getExerciseFromSelectedDate(exerciceState?.exercicesInfos, date) || null;
        };
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Actions">

    @Action(GetExercicesGeneriqueListAction)
    getExercicesGeneriqueList({patchState}: StateContext<ExerciceStateModel>, {criteres}: GetExercicesGeneriqueListAction) {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'getListExerciceGenerique', MutationGetListExerciceGeneriqueArgs>({
            mutation: gql`
                mutation getListExerciceGenerique($contexte: ContextInfosInput, $critere: CriteresExerciceDTOInput){
                    getListExerciceGenerique(
                        contexte: $contexte
                        critere: $critere
                    ){
                        ExerciceDTO{
                            Id
                            Code
                            DateDeb
                            DateFin
                            IsClos
                            DateCloture
                        }
                    }
                }
            `,
            variables: {
                critere: criteres
            }
        })
            .pipe(
                tap((getListExerciceGenerique) => {
                    patchState({
                        exercices: orderBy(getListExerciceGenerique.ExerciceDTO, ['Code'], ['desc']),
                        loading: false,
                    });
                })
            );
    }

    @Action(GetListeExerciceInfosAction)
    getListeExerciceInfos({patchState, getState}: StateContext<ExerciceStateModel>) {
        if (arrayNotEmpty(getState()?.exercicesInfos)) {
           return;
        }

        patchState({loading: true});

        return this.graphqlService.mutateOne<'GetListeExerciceInfos', MutationGetListeExerciceInfosArgs>({
            mutation: gql`
                mutation GetListeExerciceInfos($contexte: ContextInfosInput) {
                    GetListeExerciceInfos(
                        contexte: $contexte
                    ){
                        ExerciceInfos {
                            IdDelegation
                            IdExercice
                            CodeExercice
                            IsCloture
                            IsEnCoursDeCloture
                            DateDebutExercice
                            DateFinExercice
                        }
                    }
                }
            `
        })
            .pipe(
                tap((GetListeExerciceInfos) => {
                    patchState({
                        exercicesInfos: orderBy(GetListeExerciceInfos.ExerciceInfos, ['CodeExercice'], ['desc']),
                        loading: false,
                    });
                })
            );
    }

    @Action(GetListeExerciceBaseModelAction)
    getListeExerciceBaseModel({patchState}: StateContext<ExerciceStateModel>) {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'GetListeExerciceBaseModel', MutationGetListeExerciceBaseModelArgs>({
            mutation: gql`
                mutation GetListeExerciceBaseModel(
                    $contexte: ContextInfosInput,
                ) {
                    GetListeExerciceBaseModel(
                        contexte: $contexte
                    ) {
                        ExerciceModelDTO {
                            IdExercice
                            CodeExercice
                            DateDebut
                            DateFin
                        }
                    }
                }
            `,
        }).pipe(
            tap(GetListeExerciceBaseModel => patchState({exercicesBaseModel: GetListeExerciceBaseModel.ExerciceModelDTO || []}))
        );
    }

    @Action(ExerciceStateResetAction)
    resetState({patchState}: StateContext<ExerciceStateModel>) {
        patchState(new ExerciceStateModel());
    }
    // </editor-fold>
}
